// app/features/blog/components/BlogCardComponent.tsx
'use client';

import Link from 'next/link';

import { HTFBadge } from '@/app/shared/components/feedback/HTFBadge';
import { cn } from '@/lib/utils';

import type { BlogArticle } from '../blog.interface';

function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateString));
}

interface BlogCardComponentProps {
  article: BlogArticle;
}

export default function BlogCardComponent({ article }: BlogCardComponentProps) {
  const isFeatured = article.featured;

  return (
    <Link href={`/blog/${article.slug}`} className="group block">
      <article
        className={cn(
          'bg-[#F0EBE6] rounded-xl overflow-hidden shadow-sm',
          'transition-all duration-200 hover:shadow-md hover:-translate-y-0.5',
          'flex flex-col h-full',
        )}
      >
        <div className="h-1.5 bg-teal-500" />

        <div className={cn('p-5 flex flex-col flex-grow', isFeatured && 'p-6')}>
          {article.category && (
            <div className="mb-3">
              <HTFBadge variant="primary" size="sm">
                {article.category}
              </HTFBadge>
            </div>
          )}

          <h3
            className={cn(
              'font-heading font-bold tracking-tight text-gray-900 mb-2',
              'group-hover:text-teal-700 transition-colors',
              isFeatured ? 'text-xl' : 'text-lg',
            )}
          >
            {article.title}
          </h3>

          {article.summary && (
            <p className="text-sm text-text-secondary leading-relaxed line-clamp-3 mb-4 flex-grow">
              {article.summary}
            </p>
          )}

          <div className="flex items-center gap-3 text-xs text-text-secondary mt-auto pt-3">
            {article.reading_time_minutes && (
              <span>{article.reading_time_minutes} min read</span>
            )}
            {article.reading_time_minutes && article.published_at && (
              <span aria-hidden="true">·</span>
            )}
            {article.published_at && (
              <span>{formatDate(article.published_at)}</span>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
}
